import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AnalysisFeedbackCreateConflictError,
  AnalysisFeedbackReplaceConflictError,
  AnalysisFeedbackSubmission,
  AnalysisFeedbackValidationError as ApiAnalysisFeedbackValidationError
} from '@futrli-sdk/predict_analysis';

import {
  createAnalysisFeedback,
  getAnalysisFeedback,
  replaceAnalysisFeedback
} from '../../core/api/ai-insights';
import {
  AnalysisFeedbackFieldErrors,
  AnalysisFeedbackRecord,
  AnalysisFeedbackSaveResult,
  AnalysisFeedbackValidationError
} from '../components/analysis-feedback/analysis-feedback-types';

const BAD_REQUEST = 400;
const FORBIDDEN = 403;
const NOT_FOUND = 404;
const CONFLICT = 409;

type LoadResult =
  | { feedback: AnalysisFeedbackRecord; status: 'loaded' }
  | { status: 'forbidden' | 'not_found' | 'unexpected_error' };

interface UseAnalysisFeedbackResult {
  feedback: AnalysisFeedbackRecord | null;
  isAccessDenied: boolean;
  isLoading: boolean;
  loadError: boolean;
  loadFeedback: () => Promise<LoadResult>;
  saveFeedback: (submission: AnalysisFeedbackSubmission) => Promise<AnalysisFeedbackSaveResult>;
}

interface HttpErrorResponse {
  data?: unknown;
  status?: number;
  statusCode?: number;
}

interface HttpErrorLike extends HttpErrorResponse {
  cause?: unknown;
  request?: unknown;
  response?: unknown;
}

const getErrorResponse = (error: unknown): HttpErrorResponse | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorObject = error as HttpErrorLike;
  if (errorObject.response && typeof errorObject.response === 'object') {
    return errorObject.response as HttpErrorResponse;
  }

  return typeof errorObject.status === 'number' || typeof errorObject.statusCode === 'number'
    ? errorObject
    : undefined;
};

const getHttpStatus = (value: unknown): number | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const { status, statusCode } = value as HttpErrorResponse;
  return typeof status === 'number' ? status : statusCode;
};

const getMessageStatus = (error: unknown): number | undefined => {
  if (!(error instanceof Error)) {
    return undefined;
  }

  const statusMatch = /status(?: code)?\s+(\d{3})/i.exec(error.message);
  return statusMatch ? Number(statusMatch[1]) : undefined;
};

const getStatus = (error: unknown): number | undefined => {
  const errorObject = error && typeof error === 'object' ? error as HttpErrorLike : undefined;
  const requestStatus = getHttpStatus(errorObject?.request);

  return getHttpStatus(getErrorResponse(error))
    ?? (requestStatus && requestStatus > 0 ? requestStatus : undefined)
    ?? getHttpStatus(errorObject?.cause)
    ?? getMessageStatus(error);
};

const getConflictCode = (
  error: unknown
): AnalysisFeedbackCreateConflictError['code'] | AnalysisFeedbackReplaceConflictError['code'] | undefined => {
  const data = getErrorResponse(error)?.data as
    | AnalysisFeedbackCreateConflictError
    | AnalysisFeedbackReplaceConflictError
    | undefined;
  return data?.code;
};

const getValidationErrors = (error: unknown): AnalysisFeedbackFieldErrors => {
  const data = getErrorResponse(error)?.data as ApiAnalysisFeedbackValidationError | undefined;
  return data && typeof data === 'object' ? data as AnalysisFeedbackFieldErrors : {};
};

export const useAnalysisFeedback = (
  workspaceId: string,
  organisationId: string,
  analysisId: string
): UseAnalysisFeedbackResult => {
  const [feedback, setFeedback] = useState<AnalysisFeedbackRecord | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    setFeedback(null);
    setIsAccessDenied(false);
    setIsLoading(false);
    setLoadError(false);
  }, [analysisId, organisationId, workspaceId]);

  const loadFeedback = useCallback(async (): Promise<LoadResult> => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setIsLoading(true);
    setLoadError(false);

    try {
      const response = await getAnalysisFeedback(workspaceId, organisationId, analysisId);
      if (requestId !== requestIdRef.current) {
        return { status: 'unexpected_error' };
      }
      if (!response) {
        setFeedback(null);
        setIsAccessDenied(false);
        return { status: 'not_found' };
      }
      setFeedback(response);
      setIsAccessDenied(false);
      return { feedback: response, status: 'loaded' };
    } catch (error: unknown) {
      if (requestId !== requestIdRef.current) {
        return { status: 'unexpected_error' };
      }

      if (getStatus(error) === NOT_FOUND) {
        setFeedback(null);
        setIsAccessDenied(false);
        return { status: 'not_found' };
      }

      if (getStatus(error) === FORBIDDEN) {
        setIsAccessDenied(true);
        return { status: 'forbidden' };
      }

      setLoadError(true);
      return { status: 'unexpected_error' };
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [analysisId, organisationId, workspaceId]);

  const saveFeedback = useCallback(
    async (submission: AnalysisFeedbackSubmission): Promise<AnalysisFeedbackSaveResult> => {
      try {
        const response = feedback
          ? await replaceAnalysisFeedback(workspaceId, organisationId, analysisId, submission)
          : await createAnalysisFeedback(workspaceId, organisationId, analysisId, submission);
        setFeedback(response);
        return { status: 'saved' };
      } catch (error: unknown) {
        const status = getStatus(error);

        if (status === BAD_REQUEST) {
          throw new AnalysisFeedbackValidationError(getValidationErrors(error));
        }

        if (status === FORBIDDEN) {
          setIsAccessDenied(true);
          return { status: 'forbidden' };
        }

        if (status === CONFLICT) {
          const code = getConflictCode(error);
          if (code === 'stale_analysis') {
            return { status: code };
          }
          if (code === 'feedback_already_exists' || code === 'feedback_superseded') {
            const loadResult = await loadFeedback();
            if (loadResult.status === 'loaded') {
              return { feedback: loadResult.feedback, status: code };
            }
          }
        }

        throw error;
      }
    },
    [analysisId, feedback, loadFeedback, organisationId, workspaceId]
  );

  return { feedback, isAccessDenied, isLoading, loadError, loadFeedback, saveFeedback };
};
